import type {ComponentStageData, ComponentStageHeuristicDefinition} from '../../types'
import {getInnerElementsStylePointer, getStylePointer} from '../../conversionDal/utils/pointer'
import type {FontStyleValue, FontStyleEntryItems, StyleEntryItem} from '../../conversionDal/types'
import {BUTTON_FONT_SIZE_CLASSES_RULES, FONT_SIZE_CLASSES_RULES} from './constants'
import _ from 'lodash'
import {getFontSize, removeStyleProp, setFontSize, updateInnerElementsStyle, updateStyle} from '../common'
import type {FontSizeMappingHeuristicEntry} from './types'

const scaleTextFontSize = (
    componentStagedData: ComponentStageData,
    styleItemOverrides: Pick<StyleEntryItem, 'fontSize' | 'collapsibleTextButtonFontSize'>
) => updateStyle(componentStagedData, _.omitBy(styleItemOverrides, _.isNil))

const scaleTextFontSizeInInnerElements = (
    componentStagedData: ComponentStageData,
    styleItemOverrides: Pick<StyleEntryItem, 'fontSize'>
) => updateInnerElementsStyle(componentStagedData, _.omitBy(styleItemOverrides, _.isNil))

const isFontInRange = (fontStyleEntryItem: FontStyleValue, minValue: number, maxValue?: number) => {
    const size = getFontSize(fontStyleEntryItem)
    if (!size) return false
    if (size < minValue) return false
    return !(maxValue && size > maxValue)
}

const removeTextFontSize = (componentStagedData: ComponentStageData) => removeStyleProp(componentStagedData, 'fontSize')

const scaleFontSizeAccordingToMinandMax = (
    componentStageData: ComponentStageData,
    rules: FontSizeMappingHeuristicEntry[]
) => {
    const {conversionDal, componentPointer} = componentStageData
    const compStylePointer = getStylePointer(componentPointer)
    const baseStyleSettings = conversionDal.get(compStylePointer) as StyleEntryItem
    const innerElementsStylePointer = getInnerElementsStylePointer(componentPointer)
    const baseInnerElementsStyleSettings = conversionDal.get(innerElementsStylePointer) as StyleEntryItem
    const fontSizeFromComponentStyleOrTheme = _.concat(
        baseStyleSettings?.fontSize || [],
        baseStyleSettings?.theme?.fontSize || [],
        baseInnerElementsStyleSettings?.fontSize || []
    )
    if (_.isEmpty(fontSizeFromComponentStyleOrTheme)) return
    const fontSizesAfterScale: FontStyleEntryItems = []
    fontSizeFromComponentStyleOrTheme.forEach(fontStyleEntryItem => {
        const fontRule = rules.find(({minSourceFontSize, maxSourceFontSize}) =>
            isFontInRange(fontStyleEntryItem.data, minSourceFontSize, maxSourceFontSize)
        )
        if (fontRule?.scaleProportionally || !fontRule?.targetFontSize) return
        const clonedFontStyleEntryItem = _.cloneDeep(fontStyleEntryItem)
        setFontSize(clonedFontStyleEntryItem, fontRule.targetFontSize)
        fontSizesAfterScale.push(clonedFontStyleEntryItem)
    })
    if (_.isEmpty(fontSizesAfterScale)) {
        removeTextFontSize(componentStageData)
        return
    }

    const [compFontSizes, innerElementFontSizes] = _.partition(fontSizesAfterScale, fontSize => !fontSize.path)

    if (!_.isEmpty(compFontSizes)) {
        scaleTextFontSize(componentStageData, {fontSize: compFontSizes})
    }

    if (!_.isEmpty(innerElementFontSizes)) {
        scaleTextFontSizeInInnerElements(componentStageData, {fontSize: innerElementFontSizes})
    }
}

const generateCollapsibleTextButtonLabelScaleHeuristics = (): ComponentStageHeuristicDefinition[] => {
    const heuristics: ComponentStageHeuristicDefinition[] = []

    for (const {minSourceFontSize, maxSourceFontSize, targetFontSize} of BUTTON_FONT_SIZE_CLASSES_RULES) {
        heuristics.push({
            predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
                const compStylePointer = getStylePointer(componentPointer)
                const baseStyleSettings = conversionDal.get(compStylePointer) as StyleEntryItem
                const fontSizeFromComponentStyleOrTheme =
                    baseStyleSettings?.collapsibleTextButtonFontSize ??
                    baseStyleSettings?.theme?.collapsibleTextButtonFontSize

                return (
                    !!fontSizeFromComponentStyleOrTheme &&
                    fontSizeFromComponentStyleOrTheme >= minSourceFontSize &&
                    (!maxSourceFontSize || fontSizeFromComponentStyleOrTheme <= maxSourceFontSize)
                )
            },
            apply: (componentStageData: ComponentStageData) =>
                scaleTextFontSize(componentStageData, {collapsibleTextButtonFontSize: targetFontSize}),
            heuristicCode: `COLLAPSIBLE TEXT BUTTON LABEL WITH ${minSourceFontSize || '0'} < fontSize < ${
                maxSourceFontSize || 'INF'
            }`
        })
    }

    return heuristics
}

export const fontHeuristics: ComponentStageHeuristicDefinition[] = [
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            const compStylePointer = getStylePointer(componentPointer)
            const baseStyleSettings = conversionDal.get(compStylePointer) as StyleEntryItem
            const innerElementsStylePointer = getInnerElementsStylePointer(componentPointer)
            const baseInnerElementsStyleSettings = conversionDal.get(innerElementsStylePointer) as StyleEntryItem
            const fontSizeFromComponentStyleOrTheme =
                baseStyleSettings?.fontSize ||
                baseInnerElementsStyleSettings?.fontSize ||
                baseStyleSettings?.theme?.fontSize
            return fontSizeFromComponentStyleOrTheme !== undefined
        },
        apply: (componentStageData: ComponentStageData) =>
            scaleFontSizeAccordingToMinandMax(componentStageData, FONT_SIZE_CLASSES_RULES),
        heuristicCode: 'FONT SIZE'
    },
    ...generateCollapsibleTextButtonLabelScaleHeuristics()
]
